<template>
  <div>
    <base-header class="pb-6"></base-header>
    <div class="container-fluid mt--6">
      <div class="card">
        <div class="card-header border-0">
          <div class="row align-items-center">
            <div class="col">
              <h3 class="mb-0">Pesos Cocidos (En Cocido)</h3>
            </div>
            <div class="col-auto">
              <flat-picker
                v-model="date"
                :config="dateConfig"
                class="form-control"
                placeholder="Seleccionar fecha"
                @on-change="fetchData"
              />
            </div>
            <div class="col-auto">
              <base-button
                type="warning"
                size="sm"
                :loading="recalculating"
                @click="recalculate"
              >
                <i class="fas fa-sync-alt"></i> Recalcular
              </base-button>
            </div>
          </div>
        </div>

        <div class="card-body" v-if="loading">
          <div class="text-center py-5">
            <i class="fas fa-spinner fa-spin fa-2x text-primary"></i>
            <p class="mt-3">Calculando pesos cocidos...</p>
          </div>
        </div>

        <div class="card-body" v-else-if="!meals.length">
          <div class="text-center py-5">
            <i class="fas fa-exclamation-circle fa-2x text-muted"></i>
            <p class="mt-3 text-muted">No hay datos para esta fecha</p>
          </div>
        </div>

        <div class="card-body p-0" v-else>
          <div v-for="meal in meals" :key="meal.meal_id" class="mb-4">
            <el-table
              class="table-responsive table-flush"
              header-row-class-name="thead-light"
              :data="getVisibleSubgroups(meal.subgroups)"
            >
              <el-table-column :label="meal.meal_name" min-width="200px">
                <template v-slot="{ row }">
                  <strong>{{ row.subgroup_name }}</strong>
                  <span class="text-muted ml-2" v-if="row.reduction > 0">
                    ({{ row.reduction }}%)
                  </span>
                </template>
              </el-table-column>

              <el-table-column label="400 kcal" min-width="120px" align="center">
                <template v-slot="{ row }">
                  <span v-if="getCalorieData(row, 3)">
                    <strong>{{ getCalorieData(row, 3).peso_total_cocido.toFixed(1) }}</strong>
                    <small class="text-muted d-block">
                      {{ getCalorieData(row, 3).total_plates }} platos
                    </small>
                  </span>
                  <span v-else class="text-muted">-</span>
                </template>
              </el-table-column>

              <el-table-column label="600 kcal" min-width="120px" align="center">
                <template v-slot="{ row }">
                  <span v-if="getCalorieData(row, 2)">
                    <strong>{{ getCalorieData(row, 2).peso_total_cocido.toFixed(1) }}</strong>
                    <small class="text-muted d-block">
                      {{ getCalorieData(row, 2).total_plates }} platos
                    </small>
                  </span>
                  <span v-else class="text-muted">-</span>
                </template>
              </el-table-column>

              <el-table-column label="800 kcal" min-width="120px" align="center">
                <template v-slot="{ row }">
                  <span v-if="getCalorieData(row, 1)">
                    <strong>{{ getCalorieData(row, 1).peso_total_cocido.toFixed(1) }}</strong>
                    <small class="text-muted d-block">
                      {{ getCalorieData(row, 1).total_plates }} platos
                    </small>
                  </span>
                  <span v-else class="text-muted">-</span>
                </template>
              </el-table-column>

              <el-table-column label="Total Cocido" min-width="120px" align="center">
                <template v-slot="{ row }">
                  <strong class="text-success">
                    {{ row.total_peso_cocido.toFixed(1) }} gr
                  </strong>
                </template>
              </el-table-column>

              <el-table-column label="Total Crudo" min-width="120px" align="center">
                <template v-slot="{ row }">
                  <span class="text-muted">
                    {{ row.total_peso_crudo.toFixed(1) }} gr
                  </span>
                </template>
              </el-table-column>
            </el-table>

            <!-- Totals row per meal -->
            <div class="px-4 py-2 bg-light border-top">
              <div class="row">
                <div class="col">
                  <strong>Total {{ meal.meal_name }}:</strong>
                </div>
                <div class="col-auto">
                  <span class="badge badge-success badge-lg">
                    Cocido: {{ getMealTotal(meal, 'total_peso_cocido').toFixed(1) }} gr
                  </span>
                  <span class="badge badge-secondary badge-lg ml-2">
                    Crudo: {{ getMealTotal(meal, 'total_peso_crudo').toFixed(1) }} gr
                  </span>
                  <span class="badge badge-info badge-lg ml-2">
                    {{ getMealTotalPlates(meal) }} platos
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import {
  Table,
  TableColumn,
} from "element-ui";

export default {
  name: "PesosCocidosList",
  components: {
    flatPicker,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
  },
  data() {
    return {
      date: new Date().toISOString().substr(0, 10),
      dateConfig: {
        dateFormat: "Y-m-d",
      },
      loading: false,
      recalculating: false,
      meals: [],
    };
  },
  created() {
    this.fetchData();
  },
  methods: {
    fetchData() {
      this.loading = true;
      axios
        .get("cooked-weights/" + this.date)
        .then((response) => {
          this.meals = response.data.meals || [];
        })
        .catch((error) => {
          console.error("Error fetching cooked weights:", error);
          this.meals = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    recalculate() {
      this.recalculating = true;
      axios
        .post("cooked-weights/" + this.date + "/recalculate")
        .then((response) => {
          this.meals = response.data.meals || [];
        })
        .catch((error) => {
          console.error("Error recalculating:", error);
        })
        .finally(() => {
          this.recalculating = false;
        });
    },
    getVisibleSubgroups(subgroups) {
      return subgroups.filter((s) => s.show === 1);
    },
    getCalorieData(row, calorieId) {
      if (!row.calories_breakdown) return null;
      return row.calories_breakdown.find((c) => c.calorie_id === calorieId);
    },
    getMealTotal(meal, field) {
      return meal.subgroups
        .filter((s) => s.show === 1)
        .reduce((sum, s) => sum + (s[field] || 0), 0);
    },
    getMealTotalPlates(meal) {
      let total = 0;
      const seen = new Set();
      meal.subgroups.forEach((s) => {
        if (s.calories_breakdown) {
          s.calories_breakdown.forEach((c) => {
            if (!seen.has(c.calorie_id)) {
              seen.add(c.calorie_id);
              total += c.total_plates;
            }
          });
        }
      });
      return total;
    },
  },
};
</script>

<style scoped>
.badge-lg {
  font-size: 0.85rem;
  padding: 0.4em 0.8em;
}
</style>
